import clsx from 'clsx';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import CustomImage from '@/components/CustomImage';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { EUserRole } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import { useSettingsStore } from '@/store/useSettingsStore';
import { IChildren } from '@/types';
import { isValidUserRole } from '@/utils';

import { AuthLayoutContext } from './authContext';

const ROLE_QUERY_PARAM = 'role';
const ROLE_STORAGE_KEY = 'auth-role';
const ROLE_FLASH_MS = 800;

const ROLE_BG_IMAGE: Record<EUserRole, string> = {
  [EUserRole.PARENT]: '/assets/images/auth-bg-parent.png',
  [EUserRole.SCHOOL]: '/assets/images/auth-bg-school.png',
  [EUserRole.TEACHER]: '/assets/images/auth-bg-teacher.png',
};

const ROLE_CTA_KEY: Record<EUserRole, string> = {
  [EUserRole.PARENT]: 'auth-layout.cta.parent',
  [EUserRole.SCHOOL]: 'auth-layout.cta.school',
  [EUserRole.TEACHER]: 'auth-layout.cta.teacher',
};

const readStoredRole = (): EUserRole => {
  const stored = globalThis.sessionStorage?.getItem(ROLE_STORAGE_KEY);
  return isValidUserRole(stored) ? stored : EUserRole.SCHOOL;
};

const AuthLayout = ({ children }: IChildren) => {
  const { settings } = useSettingsStore();
  const { formatText } = useFormatText();
  const [searchParams] = useSearchParams();

  const [role, setRoleState] = useState<EUserRole>(() => {
    const urlRole = searchParams.get(ROLE_QUERY_PARAM);
    if (isValidUserRole(urlRole)) {
      globalThis.sessionStorage?.setItem(ROLE_STORAGE_KEY, urlRole);
      return urlRole;
    }
    return readStoredRole();
  });

  // Adopt URL role on deep-link arrivals (e.g. reset-password from email).
  useEffect(() => {
    const urlRole = searchParams.get(ROLE_QUERY_PARAM);
    if (!isValidUserRole(urlRole)) return;
    const stored = globalThis.sessionStorage?.getItem(ROLE_STORAGE_KEY);
    if (stored !== urlRole) {
      globalThis.sessionStorage?.setItem(ROLE_STORAGE_KEY, urlRole);
      setRoleState(urlRole);
    }
  }, [searchParams]);

  const setRole = useCallback((next: EUserRole) => {
    globalThis.sessionStorage?.setItem(ROLE_STORAGE_KEY, next);
    setRoleState(next);
  }, []);

  const contextValue = useMemo(() => ({ role, setRole }), [role, setRole]);

  const [flash, setFlash] = useState(false);
  const previousRoleRef = useRef<EUserRole | null>(null);

  useEffect(() => {
    if (previousRoleRef.current !== null && previousRoleRef.current !== role) {
      setFlash(true);
      const timer = globalThis.setTimeout(() => setFlash(false), ROLE_FLASH_MS);
      previousRoleRef.current = role;
      return () => globalThis.clearTimeout(timer);
    }
    previousRoleRef.current = role;
  }, [role]);

  const isArabic = settings?.language === 'ar';
  const brandLogoSrc = isArabic
    ? '/assets/images/tirtir-world-ar.png'
    : '/assets/images/tirtir-world-en.png';
  const backgroundImage = ROLE_BG_IMAGE[role];
  const ctaLabel = formatText(ROLE_CTA_KEY[role]);

  return (
    <AuthLayoutContext.Provider value={contextValue}>
      <div className="flex h-screen w-screen" dir="ltr">
        <div
          className="relative w-[60%] m-6 max-md:hidden overflow-hidden rounded-2xl bg-cover bg-center transition-[background-image] duration-300"
          style={{ backgroundImage: `url('${backgroundImage}')` }}
        >
          <CustomImage
            src={brandLogoSrc}
            alt={formatText('auth-layout.brand-alt')}
            className="absolute top-6 left-6 h-30 w-auto select-none pointer-events-none"
          />
          <span
            className={clsx(
              'absolute top-6 right-6 inline-flex items-center rounded-[10px] bg-brand-default px-5 py-2 text-xl font-bold tracking-[-0.24px] text-white select-none origin-right transition-all duration-500 ease-out',
              flash && 'scale-110 shadow-lg ring-2 ring-white/60'
            )}
          >
            {ctaLabel}
          </span>
        </div>
        <div
          className="relative w-[40%] max-md:w-full overflow-y-auto min-h-screen flex flex-col justify-center items-center"
          dir={settings?.direction}
        >
          <div className="absolute top-6 end-6 z-50">
            <LanguageSwitcher />
          </div>
          {children}
        </div>
      </div>
    </AuthLayoutContext.Provider>
  );
};

export default AuthLayout;
